// Package Imports
import React, { useEffect } from 'react';
import {
  useThemedLayoutContext,
  type RefineThemedLayoutV2SiderProps,
} from '@refinedev/antd';
import {
  CanAccess,
  ITreeMenu,
  pickNotDeprecated,
  useActiveAuthProvider,
  useLink,
  useLogout,
  useMenu,
  useRefineContext,
  useRouterContext,
  useRouterType,
  useTranslate,
} from '@refinedev/core';

// Project Imports;
import { Button, Divider, Drawer, Grid, Layout, Menu, theme } from 'antd';
import {
  BarsOutlined,
  DashboardOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Images } from 'core/common/imagePath';
import './index.css';
import resourceName from 'core/common/resourceName';

const { SubMenu } = Menu;
const { useToken } = theme;

const CustomSider: React.FC<RefineThemedLayoutV2SiderProps> = () => {
  const { token } = useToken();
  const t = useTranslate();
  const {
    siderCollapsed,
    setSiderCollapsed,
    mobileSiderOpen,
    setMobileSiderOpen,
  } = useThemedLayoutContext();
  useEffect(() => {
    setSiderCollapsed(true);
  }, []);
  const routerType = useRouterType();
  const NewLink = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const Link = routerType === 'legacy' ? LegacyLink : NewLink;
  const translate = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const breakpoint = Grid.useBreakpoint();
  const { hasDashboard } = useRefineContext();
  const authProvider = useActiveAuthProvider();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  const isMobile =
    typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;

  const renderTreeView = (tree: ITreeMenu[], selectedKeyy?: string) =>
    tree.map((item: ITreeMenu) => {
      const { icon, route, key, name, children, parentName, meta, options } =
        item;
      if (children.length > 0) {
        return (
          <SubMenu
            key={item.key}
            // icon={icon ?? <UnorderedListOutlined />}
            icon={(() => {
              switch (name) {
                case resourceName.workflow:
                case resourceName.categoryManagement:
                  return Images.workflowIcon;
                case resourceName.bulkRule:
                  return Images.bulkRuleIcon;
                default:
                  return '';
              }
            })()}
            title={
              <div
                tabIndex={0}
                style={{ outline: 'none', width: '100%', display: 'block' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    (
                      e.currentTarget.closest(
                        '.ant-menu-submenu-title'
                      ) as HTMLElement
                    )?.click();
                  }
                }}
                title={t(`${item.name}.${item.name}`)}
              >
                {t(`${item.name}.${item.name}`)}
              </div>
            }
          >
            {renderTreeView(children, selectedKeyy)}
          </SubMenu>
        );
      }
      const isSelected = key === selectedKeyy;
      const isRoute = !(
        pickNotDeprecated(meta?.parent, options?.parent, parentName) !==
          undefined && children.length === 0
      );

      return (
        <CanAccess
          key={item.key}
          resource={name.toLowerCase()}
          action="list"
          params={{
            resource: item,
          }}
        >
          <Menu.Item
            key={item.key}
            icon={icon ?? (isRoute && <UnorderedListOutlined />)}
          >
            <Link title={t(`${item.name}.${item.name}`)} to={route ?? ''}>
              {t(`${item.name}.${item.name}`)}
            </Link>
            {!siderCollapsed && isSelected ? (
              <div className="ant-menu-tree-arrow" />
            ) : null}
          </Menu.Item>
        </CanAccess>
      );
    });

  const dashboard = hasDashboard ? (
    <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
      <Link to="/">{translate('dashboard.title', 'Dashboard')}</Link>
      {!siderCollapsed && selectedKey === '/' ? (
        <div className="ant-menu-tree-arrow" />
      ) : null}
    </Menu.Item>
  ) : null;

  const renderSider = () => (
    <>
      {dashboard}
      {renderTreeView(menuItems, selectedKey)}
    </>
  );

  const renderMenu = () => (
    <Menu
      selectedKeys={selectedKey ? [selectedKey] : []}
      defaultOpenKeys={defaultOpenKeys}
      mode="inline"
      className="custom-menu"
      onClick={() => {
        setMobileSiderOpen(false);
      }}
    >
      {renderSider()}
    </Menu>
  );
  const renderDrawerSider = () => (
    <>
      <Drawer
        open={mobileSiderOpen}
        onClose={() => setMobileSiderOpen(false)}
        placement="left"
        closable={false}
        width={200}
        bodyStyle={{
          padding: 0,
        }}
        maskClosable={true}
      >
        <Layout>
          <Layout.Sider className="layout-slider">
            <div className="layout-slider-wrap">
              {siderCollapsed ? (
                <img src={Images.AugmentLogoSmall} />
              ) : (
                <img className="px-2" src={Images.AugmentLogo} />
              )}
            </div>
            {renderMenu()}
          </Layout.Sider>
        </Layout>
      </Drawer>
      <Button
        className="layout-slider-button"
        size="large"
        onClick={() => setMobileSiderOpen(true)}
        icon={<BarsOutlined />}
      />
    </>
  );

  if (isMobile) {
    return renderDrawerSider();
  }

  const siderStyles: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    borderRight: `1px solid ${token.colorBgElevated}`,
  };
  const handleLogout = () => {
    mutateLogout();
  };
  return (
    <Layout.Sider
      style={siderStyles}
      collapsible
      collapsed={!siderCollapsed}
      // collapsed={false}
      // onCollapse={(collapsed, type) => {
      //   if (type === 'clickTrigger') {
      //     setSiderCollapsed(!collapsed);
      //   }
      // }}
      collapsedWidth={140}
      breakpoint="lg"
      trigger={
        <>
          <Divider className="m-0" />
          <Button
            className="w-full flex  px-4 items-center gap-4 border-0  logout-button"
            type="text"
            onClick={handleLogout}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5.636 5.636a9 9 0 1 0 12.728 0M12 3v9"
                />
              </svg>
            }
          >
            {siderCollapsed ? 'Logout' : ''}
          </Button>
        </>
      }
    >
      {/* <div className={`sider-logo ${!siderCollapsed ? 'collapsed' : ''}`}>
        {!siderCollapsed ? (
          <img width="40px" src={Images.AugmentLogoSmall} />
        ) : (
          <img height="30px" className="px-2" src={Images.AugmentLogo} />
        )}
      </div> */}
      <div className={`sider-logo ${!siderCollapsed ? 'collapsed' : ''}`}>
        {!siderCollapsed ? (
          <div className="w-full gap-5 justify-center flex">
            <img width="40px" className="ml-9" src={Images.AugmentLogoSmall} />
            <Button
              type="text"
              onClick={() => setSiderCollapsed(!siderCollapsed)}
              icon={
                <img
                  className="object-contain"
                  width="20px"
                  src={Images.rightArrow}
                />
              }
            />
          </div>
        ) : (
          <div className="flex justify-between w-full items-center">
            <img height="65px" className="px-2" src={Images.AugmentLogo} />
            <Button
              type="text"
              icon={
                !siderCollapsed ? (
                  <img width="20px" src={Images.rightArrow} />
                ) : (
                  <img width="20px" src={Images.leftArrow} />
                )
              }
              onClick={() => setSiderCollapsed(!siderCollapsed)}
            />
          </div>
        )}
      </div>
      {renderMenu()}
    </Layout.Sider>
  );
};

export default CustomSider;
